import { supabase } from '../lib/supabase/client';
import { MembershipApplication } from '../types/models';
import { Database } from '../types/database.types';

type ApplicationRow = Database['public']['Tables']['membership_applications']['Row'];
type ApplicationInsert = Database['public']['Tables']['membership_applications']['Insert'];
type ApplicationUpdate = Database['public']['Tables']['membership_applications']['Update'];

interface ApplicationResponse {
  data: MembershipApplication[] | null;
  error: Error | null;
}

interface SingleApplicationResponse {
  data: MembershipApplication | null;
  error: Error | null;
}

// Convert database row to camelCase model
const toApplicationModel = (row: ApplicationRow): MembershipApplication => ({
  id: row.id,
  applicantId: row.applicant_id,
  fullName: row.full_name,
  email: row.email,
  phone: row.phone,
  occupation: row.occupation,
  netWorth: row.net_worth,
  memberReferences: row.member_references,
  qualificationScore: row.qualification_score,
  submissionDate: row.submission_date,
  reviewDate: row.review_date,
  status: row.status,
  adminNotes: row.admin_notes,
  decisionRationale: row.decision_rationale,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

export const membershipService = {
  async getAll(): Promise<ApplicationResponse> {
    try {
      const { data, error } = await supabase
        .from('membership_applications')
        .select('*')
        .order('submission_date', { ascending: false });

      if (error) throw error;

      return {
        data: (data || []).map(toApplicationModel),
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async getByStatus(status: string): Promise<ApplicationResponse> {
    try {
      const { data, error } = await supabase
        .from('membership_applications')
        .select('*')
        .eq('status', status)
        .order('submission_date', { ascending: false });

      if (error) throw error;

      return {
        data: (data || []).map(toApplicationModel),
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async getById(id: string): Promise<SingleApplicationResponse> {
    try {
      const { data, error } = await supabase
        .from('membership_applications')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        data: data ? toApplicationModel(data) : null,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async createApplication(applicationData: {
    fullName: string;
    email: string;
    phone: string;
    occupation: string;
    netWorth: string;
    memberReferences: string;
  }): Promise<SingleApplicationResponse> {
    try {
      // Create membership application WITHOUT requiring authentication
      // applicant_id is now nullable, so we don't need to create/get user
      const insertData: ApplicationInsert = {
        applicant_id: null, // Anonymous application - no user account required
        full_name: applicationData.fullName,
        email: applicationData.email,
        phone: applicationData.phone,
        occupation: applicationData.occupation,
        net_worth: applicationData.netWorth,
        member_references: applicationData.memberReferences,
        status: 'pending',
        qualification_score: 0,
        submission_date: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('membership_applications')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data ? toApplicationModel(data) : null,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async createApplicationPayment(applicationId: string, transactionId: string): Promise<{
    data: any | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('application_payments')
        .insert({
          application_id: applicationId,
          amount: 150.00,
          payment_status: 'completed',
          transaction_id: transactionId,
          payment_date: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async updateStatus(
    id: string,
    status: 'pending' | 'under_review' | 'approved' | 'rejected',
    adminNotes?: string,
    decisionRationale?: string
  ): Promise<SingleApplicationResponse> {
    try {
      const updateData: ApplicationUpdate = {
        status,
        review_date: new Date().toISOString(),
        admin_notes: adminNotes,
        decision_rationale: decisionRationale,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('membership_applications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data ? toApplicationModel(data) : null,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async updateQualificationScore(
    id: string,
    score: number
  ): Promise<SingleApplicationResponse> {
    try {
      const { data, error } = await supabase
        .from('membership_applications')
        .update({
          qualification_score: score,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data: data ? toApplicationModel(data) : null,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async getApplicationStats(): Promise<{
    data: {
      total: number;
      pending: number;
      underReview: number;
      approved: number;
      rejected: number;
    } | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('membership_applications')
        .select('status');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        pending: data?.filter((a) => a.status === 'pending').length || 0,
        underReview: data?.filter((a) => a.status === 'under_review').length || 0,
        approved: data?.filter((a) => a.status === 'approved').length || 0,
        rejected: data?.filter((a) => a.status === 'rejected').length || 0,
      };

      return { data: stats, error: null };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },

  async getApplicationWithPayment(applicationId: string): Promise<{
    data: {
      application: MembershipApplication;
      payment: any | null;
    } | null;
    error: Error | null;
  }> {
    try {
      // Fetch application
      const { data: appData, error: appError } = await supabase
        .from('membership_applications')
        .select('*')
        .eq('id', applicationId)
        .single();

      if (appError) throw appError;

      // Fetch payment if exists
      const { data: paymentData, error: paymentError } = await supabase
        .from('application_payments')
        .select('*')
        .eq('application_id', applicationId)
        .single();

      // Payment might not exist yet, so don't throw on error
      return {
        data: {
          application: appData ? toApplicationModel(appData) : null,
          payment: paymentData || null,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error as Error,
      };
    }
  },
};